import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QrCode, Download, Copy, Share2, ExternalLink, Loader2, CheckCircle2, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CAPTIONS = [
  "We're on HomeRange — everything hunting, one place.",
  "Find us on HomeRange 🏔️",
  "Proud HomeRange Partner.",
  "Explore our HomeRange profile and see what we're up to.",
];

function Copyable({ label, value, testid }) {
  const copy = async () => { await navigator.clipboard.writeText(value); toast.success(`${label} copied`); };
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-saddle">{label}</p>
      <div className="flex items-center gap-2 mt-1">
        <input readOnly value={value || ""} className="flex-1 border border-saddle/25 bg-paper px-3 py-2 rounded-lg text-sm text-charcoal/80 truncate" data-testid={`${testid}-input`} />
        <button onClick={copy} className="p-2.5 border border-saddle/25 rounded-lg text-charcoal/70 hover:text-blaze hover:border-blaze transition-colors" aria-label={`Copy ${label}`} data-testid={`${testid}-copy`}><Copy size={16} /></button>
      </div>
    </div>
  );
}

export default function PartnerMarketing() {
  const [p, setP] = useState(undefined);

  useEffect(() => { api.partnerMe().then((d) => setP(d.partner)).catch(() => setP(null)); }, []);

  if (p === undefined) return <div className="bg-paper min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div></div>;

  if (!p) return (
    <div className="bg-paper min-h-screen"><SiteHeader />
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <Megaphone size={32} className="mx-auto text-saddle/50" />
        <h1 className="font-serif text-3xl text-forest mt-4">No partner profile yet</h1>
        <p className="text-charcoal/60 mt-2">Create your HomeRange business account to unlock your profile URL, QR code and marketing tools.</p>
        <Link to="/business" className="inline-block mt-4 bg-blaze text-white px-5 py-2.5 rounded-full font-semibold">Get started</Link>
      </div><Footer />
    </div>
  );

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const profileUrl = origin + (p.public_path || "");
  const referralUrl = origin + (p.referral_link && p.referral_link.startsWith("/") ? p.referral_link : `/r/${p.referral_code}`);
  const qrPng = `${BACKEND}/api/partners/${p.id}/qr.png?box=16`;
  const share = async () => { try { if (navigator.share) await navigator.share({ title: "My HomeRange profile", url: profileUrl }); else { await navigator.clipboard.writeText(profileUrl); toast.success("Copied"); } } catch { /* */ } };

  return (
    <div className="bg-paper min-h-screen" data-testid="partner-marketing">
      <SiteHeader />
      <main className="max-w-[900px] mx-auto px-5 sm:px-8 py-10">
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-blaze">Marketing Tools</span>
        <h1 className="font-serif text-4xl text-forest mt-1">Grow with HomeRange</h1>

        {/* Completion */}
        <div className="mt-6 border border-saddle/20 bg-white rounded-xl p-5" data-testid="partner-completion">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-forest">Your profile: {p.completion}% complete</span>
            {p.completion >= 100 ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><CheckCircle2 size={14} /> Complete</span>
              : <Link to="/business" className="text-blaze text-xs font-semibold">Finish setup <ExternalLink size={11} className="inline" /></Link>}
          </div>
          <div className="h-2 bg-paper rounded-full mt-2 overflow-hidden"><div className="h-full bg-blaze transition-all" style={{ width: `${p.completion}%` }} /></div>
        </div>

        <div className="grid sm:grid-cols-[1fr_240px] gap-6 mt-6">
          <div className="space-y-4">
            <Copyable label="Your HomeRange URL" value={profileUrl} testid="mkt-profile-url" />
            <Copyable label="Your referral link" value={referralUrl} testid="mkt-referral-url" />
            <div className="flex gap-2">
              <a href={profileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blaze"><ExternalLink size={15} /> View public profile</a>
              <button onClick={share} className="inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal/70 hover:text-blaze ml-auto" data-testid="mkt-share"><Share2 size={15} /> Share</button>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-saddle">Suggested captions</p>
              <ul className="mt-2 space-y-1.5">
                {CAPTIONS.map((c) => (
                  <li key={c}><button onClick={() => { navigator.clipboard.writeText(c); toast.success("Caption copied"); }} className="text-left text-sm text-charcoal/75 hover:text-blaze w-full">“{c}”</button></li>
                ))}
              </ul>
            </div>
          </div>

          {/* QR */}
          <div className="border border-saddle/20 bg-white rounded-xl p-4 text-center h-fit" data-testid="mkt-qr">
            <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-saddle"><QrCode size={13} /> Your QR code</div>
            <img src={qrPng} alt="Your HomeRange QR code" className="w-full rounded-lg border border-saddle/15 mt-3" data-testid="mkt-qr-image" />
            <div className="flex gap-2 mt-3">
              <a href={qrPng} download className="flex-1 inline-flex items-center justify-center gap-1 bg-forest text-bone px-2 py-2 rounded-full text-xs font-semibold" data-testid="mkt-qr-png"><Download size={13} /> PNG</a>
              <a href={`${BACKEND}/api/partners/${p.id}/qr.svg`} download className="flex-1 inline-flex items-center justify-center gap-1 border border-saddle/25 px-2 py-2 rounded-full text-xs font-semibold text-charcoal/80"><Download size={13} /> SVG</a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
